// Function to create an optimized version of the image

/**
 * Optimize an image by resizing it to a smaller size while maintaining aspect ratio and image quality.
 * The optimization process has a 25 second timeout and will reject the promise if it takes longer.
 * If the image is too large for available memory, it will be resized more aggressively.
 * #pictures
 * @param {File} file - The image file to optimize.
 * @returns {Promise<File>} - A promise that resolves to the optimized image file.
 */
export const optimizeImage = async (file) => {
  // If it's not an image, return the original file
  if (!file.type.startsWith('image/')) {
    console.log("File is not an image, returning original");
    return file;
  }

  console.log("Starting image optimization for:", file.name, "Size:", file.size);

  return new Promise((resolve, reject) => {
    // Set up timeout for the entire optimization process
    const timeoutId = setTimeout(() => {
      console.error("Image optimization timeout");
      reject(new Error('Image optimization timeout'));
    }, 25000); // 25 second timeout

    const reader = new FileReader();
    
    reader.onerror = () => {
      clearTimeout(timeoutId);
      console.error("FileReader error");
      reject(new Error('Failed to read file'));
    };
    
    reader.onload = (event) => {
      try {
        const img = new Image();
        
        img.onerror = () => {
          clearTimeout(timeoutId);
          console.error("Image loading error");
          reject(new Error('Failed to load image'));
        };
        
        img.onload = () => {
          try {
            console.log("Original image dimensions:", img.width, "x", img.height);
            
            // Check if canvas is available (some mobile browsers may have issues)
            if (!document.createElement || typeof document.createElement !== 'function') {
              clearTimeout(timeoutId);
              console.warn("Canvas not available, returning original file");
              resolve(file);
              return;
            }

            const canvas = document.createElement('canvas');
            let width = img.width;
            let height = img.height;

            // More aggressive size limits for mobile devices
            const MAX_WIDTH = 1200;
            const MAX_HEIGHT = 1200;
            
            // Check available memory (rough estimate)
            const estimatedMemoryUsage = width * height * 4; // 4 bytes per pixel
            const MAX_MEMORY = 50 * 1024 * 1024; // 50MB limit
            
            if (estimatedMemoryUsage > MAX_MEMORY) {
              console.warn("Image too large for memory, reducing size more aggressively");
              const reductionFactor = Math.sqrt(MAX_MEMORY / estimatedMemoryUsage);
              width = Math.round(width * reductionFactor);
              height = Math.round(height * reductionFactor);
            }

            // Calculate new dimensions while maintaining aspect ratio
            if (width > height) {
              if (width > MAX_WIDTH) {
                height = Math.round((height * MAX_WIDTH) / width);
                width = MAX_WIDTH;
              }
            } else {
              if (height > MAX_HEIGHT) {
                width = Math.round((width * MAX_HEIGHT) / height);
                height = MAX_HEIGHT;
              }
            }

            console.log("Optimized dimensions:", width, "x", height);

            canvas.width = width;
            canvas.height = height;

            // Create a context with error handling
            const ctx = canvas.getContext('2d', { alpha: true });
            if (!ctx) {
              clearTimeout(timeoutId);
              console.error("Failed to get canvas context");
              reject(new Error('Canvas context not available'));
              return;
            }
            
            // Clear the canvas with a transparent background
            ctx.clearRect(0, 0, width, height);
            
            // Draw the image with high quality settings
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            
            try {
              ctx.drawImage(img, 0, 0, width, height);
            } catch (drawError) {
              clearTimeout(timeoutId);
              console.error("Error drawing image to canvas:", drawError);
              reject(new Error('Failed to draw image to canvas'));
              return;
            }

            // Determine if the original image is PNG (to preserve transparency)
            const isPNG = file.type === 'image/png';
            
            // Convert to blob with appropriate format and quality
            try {
              canvas.toBlob((blob) => {
                clearTimeout(timeoutId);
                
                if (!blob) {
                  console.error("Failed to create blob from canvas");
                  reject(new Error('Failed to create optimized image'));
                  return;
                }
                
                // Create a new file from the blob
                const optimizedFile = new File([blob], file.name, {
                  // Keep PNG for PNG images to preserve transparency, use JPEG for others
                  type: isPNG ? 'image/png' : 'image/jpeg',
                  lastModified: Date.now(),
                });
                
                console.log("Image optimization successful. Original:", file.size, "Optimized:", optimizedFile.size);
                resolve(optimizedFile);
              }, isPNG ? 'image/png' : 'image/jpeg', isPNG ? 1.0 : 0.8); // Use appropriate quality settings
            } catch (blobError) {
              clearTimeout(timeoutId);
              console.error("Error creating blob:", blobError);
              reject(new Error('Failed to create blob from canvas'));
            }
          } catch (canvasError) {
            clearTimeout(timeoutId);
            console.error("Canvas processing error:", canvasError);
            reject(new Error('Canvas processing failed'));
          }
        };
        
        img.src = event.target.result;
      } catch (imgError) {
        clearTimeout(timeoutId);
        console.error("Image processing error:", imgError);
        reject(new Error('Image processing failed'));
      }
    };
    
    try {
      reader.readAsDataURL(file);
    } catch (readerError) {
      clearTimeout(timeoutId);
      console.error("FileReader start error:", readerError);
      reject(new Error('Failed to start reading file'));
    }
  });
};
